/**
 * QA-IMPERSONATION
 *
 * Lets QA read the app as any technician without that technician's credentials, so the
 * Parts tab and the schedule are exercised against the real resolver chain rather than
 * hard-coded fallbacks.
 *
 * The switch is OFF here, deliberately. Flip it to true only on a branch that does not
 * reach master — enabling it is a deploy, which is the intended friction. The backend
 * has its own switch and refuses the header in production either way.
 *
 * `grep -rn QA-IMPERSONATION` finds every line of this feature.
 */
const QA_IMPERSONATION_SWITCH = false;

/** REACT_APP_STAGE values that mean a production build. */
export enum ProductionStage {
  Production = "production",
  Prod = "prod",
}

// Denylist on purpose: UI-only (the backend decides access) and Vercel preview stage names are unknown; unset = off.
export const isQaImpersonationAllowed = (enabled: boolean, stage?: string): boolean => {
  const normalized = (stage ?? "").trim().toLowerCase();
  return enabled && !!normalized && !Object.values<string>(ProductionStage).includes(normalized);
};

export const QA_IMPERSONATION_ENABLED = isQaImpersonationAllowed(QA_IMPERSONATION_SWITCH, process.env.REACT_APP_STAGE);

/** Must match QA_IMPERSONATION_HEADER in the backend. */
export const QA_IMPERSONATION_HEADER = "x-qa-impersonate-technician";

const STORAGE_KEY = "nubrakes:qa-impersonated-technician:v1";

export type ImpersonatedTechnician = {
  id: string;
  name: string;
  market: string;
};

/**
 * Read by the Apollo link on every request, so it cannot be React state. localStorage
 * throws in a locked-down browser, in which case impersonation simply never engages.
 */
export const readImpersonation = (): ImpersonatedTechnician | null => {
  if (!QA_IMPERSONATION_ENABLED) return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.id ? (parsed as ImpersonatedTechnician) : null;
  } catch {
    // Unparseable or unreadable: behave as "not impersonating".
    return null;
  }
};

export const writeImpersonation = (technician: ImpersonatedTechnician | null) => {
  try {
    if (technician) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(technician));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the choice still holds for this page load.
  }
};
